import { isDictatedReport } from "@rosc/contracts";
import { Button, IconButton } from "@rosc/ui/v32/button";
import { ConfirmSheet } from "@rosc/ui/v32/confirm-sheet";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from "@rosc/ui/v32/menu";
import { Notice } from "@rosc/ui/v32/notice";
import { DOCK_MAX_WIDTH, RecordDock } from "@rosc/ui/v32/record-button";
import { formatClock, Waveform } from "@rosc/ui/v32/recorder-bar";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CloudOff,
  Copy,
  FileText,
  Loader2,
  Mic,
  RotateCcw,
  Settings2,
  Trash2,
  Undo2,
} from "lucide-react";
import type { JSX, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BetaFeedbackButton, BetaMark } from "@/components/ui/beta/Beta";
import { dictationMessage } from "@/components/ui/DictationButton";
import {
  type ReportSaveStatus,
  reportStateFromVisit,
  useDictatedReportSaving,
} from "@/hooks/dictation/useDictatedReportSaving";
import {
  type DictationReportPhase,
  type DictationReportState,
  useDictationReport,
} from "@/hooks/dictation/useDictationReport";
import { TranscriptionProviderName } from "@/hooks/transcriber/types";
import { useDictation } from "@/hooks/transcriber/useDictation";
import { toast } from "@/hooks/use-toast";
import {
  type DictationTemplate,
  deleteDictatedReport,
  useDictationTemplates,
} from "@/lib/dictation-templates";
import { getShortDateAndTime } from "@/lib/time";
import { cn } from "@/lib/utils";
import { useVisitList } from "@/lib/visit-queries";
import { DictationTemplatesSheetV32 } from "./DictationTemplatesSheetV32";

const RECENT_REPORTS = 5;

// A full report dictation can be long; the field dictations stop at 3 min.
export const DICTATION_REPORT_MAX_SECONDS = 600;

const CHIP =
  "inline-flex h-10 min-w-0 max-w-full items-center gap-1.5 rounded-full bg-v3-panel px-3 font-v3 text-[13.5px] font-semibold text-v3-text shadow-v3-hairline-strong transition-colors hover:bg-v3-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:opacity-60 data-[state=open]:bg-v3-surface-2";

/** The report text, changed lines highlighted (presentational). */
export const DictationReportView = ({
  report,
  changedLines,
  lastChangedLines,
  busy = false,
}: {
  report: string;
  changedLines: readonly number[];
  lastChangedLines: readonly number[];
  busy?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const changed = new Set(changedLines);
  const last = new Set(lastChangedLines);
  return (
    <div
      aria-busy={busy}
      aria-label={t("dictationReport.report")}
      className={cn(
        "rounded-[14px] bg-v3-panel px-1.5 py-2 text-[15px] leading-[22px] text-v3-text shadow-v3-hairline-strong transition-opacity sm:px-2",
        busy && "opacity-60",
      )}
      data-testid="dictation-report"
      role="document"
    >
      {report.split("\n").map((line, index) =>
        line.trim() ? (
          <p
            // Lines are positional; the report is replaced as a whole.
            // biome-ignore lint/suspicious/noArrayIndexKey: see above
            key={index}
            className={cn(
              "whitespace-pre-wrap break-words rounded-lg px-2.5 py-0.5",
              changed.has(index) && "bg-v3-brand-tint",
              last.has(index) && "shadow-[inset_3px_0_0_0_var(--v3-brand)]",
            )}
            data-changed={changed.has(index) || undefined}
          >
            {line}
          </p>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: see above
          <span key={index} aria-hidden className="block h-2.5" />
        ),
      )}
    </div>
  );
};

/** The template chip: the current template, chosen by voice or here. */
export const DictationTemplateChip = ({
  templates,
  state,
  disabled,
  canUndo,
  onChoose,
  onUndo,
  onManage,
}: {
  templates: readonly DictationTemplate[];
  state: DictationReportState;
  disabled?: boolean;
  canUndo: boolean;
  onChoose: (template: DictationTemplate) => void;
  onUndo: () => void;
  onManage: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 items-center gap-1">
      <Menu>
        <MenuTrigger asChild>
          <button
            aria-label={t("dictationReport.template.aria", {
              name: state.template?.name ?? t("dictationReport.template.none"),
            })}
            className={CHIP}
            data-testid="dictation-template-chip"
            disabled={disabled}
            type="button"
          >
            <FileText aria-hidden className="size-4 shrink-0" />
            <span className="min-w-0 truncate">
              {state.template?.name ?? t("dictationReport.template.none")}
            </span>
            {state.pickedBy === "voice" && (
              <span className="shrink-0 font-medium text-v3-text-3">
                · {t("dictationReport.template.byVoice")}
              </span>
            )}
            <ChevronDown
              aria-hidden
              className="size-4 shrink-0 text-v3-text-3"
            />
          </button>
        </MenuTrigger>
        <MenuContent
          align="start"
          className="max-h-[min(60dvh,420px)] w-[min(320px,calc(100vw-32px))] overflow-y-auto"
        >
          <MenuLabel>{t("dictationReport.template.menu")}</MenuLabel>
          <MenuRadioGroup value={state.template?.id ?? ""}>
            {templates.map((template) => (
              <MenuRadioItem
                key={template.id}
                value={template.id}
                onSelect={() => onChoose(template)}
              >
                <span className="min-w-0">
                  <span className="block truncate">{template.name}</span>
                  {template.aliases[0] && (
                    <span className="block truncate text-[12.5px] text-v3-text-3">
                      “{template.aliases[0]}”
                    </span>
                  )}
                </span>
              </MenuRadioItem>
            ))}
          </MenuRadioGroup>
          {templates.length > 0 && <MenuSeparator />}
          <MenuItem
            data-testid="dictation-manage-templates"
            onSelect={onManage}
          >
            <Settings2 aria-hidden />
            {t("dictationReport.template.manage")}
          </MenuItem>
        </MenuContent>
      </Menu>
      {canUndo && (
        <Button
          className="max-sm:w-11 max-sm:px-0"
          data-testid="dictation-undo"
          disabled={disabled}
          title={t("dictationReport.undo")}
          variant="ghost"
          onClick={onUndo}
        >
          <Undo2 />
          <span className="max-sm:sr-only">{t("dictationReport.undo")}</span>
        </Button>
      )}
    </div>
  );
};

const GuardNotice = ({
  state,
}: {
  state: DictationReportState;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const guard = state.guard;
  if (!guard || guard.pass) return null;
  const parts: string[] = [];
  if (guard.missing.length)
    parts.push(
      t("dictationReport.guard.missing", { values: guard.missing.join(", ") }),
    );
  if (guard.unexpected.length)
    parts.push(
      t("dictationReport.guard.unexpected", {
        values: guard.unexpected.join(", "),
      }),
    );
  if (guard.duplicated.length)
    parts.push(
      t("dictationReport.guard.duplicated", {
        values: guard.duplicated.join(", "),
      }),
    );
  if (guard.notStated?.length)
    parts.push(
      t("dictationReport.guard.notStated", {
        values: guard.notStated.join(", "),
      }),
    );
  if (guard.format) parts.push(t("dictationReport.guard.format"));
  return (
    <Notice
      description={parts.join(" ")}
      testId="dictation-guard"
      title={t("dictationReport.guard.title")}
      tone="warn"
    />
  );
};

/** What to say first, before anything was dictated. */
export type RecentReport = { id: string; title: string; when: string };

/** The last saved reports, to open again (Dictated report intro). */
const RecentReports = ({
  reports,
  onOpen,
}: {
  reports: readonly RecentReport[];
  onOpen?: (id: string) => void;
}): JSX.Element | null => {
  const { t } = useTranslation();
  if (!reports.length || !onOpen) return null;
  return (
    <section className="mt-8" data-testid="dictation-recent">
      <h3 className="px-1 text-[13px] font-semibold text-v3-text-3">
        {t("dictationReport.recent.title")}
      </h3>
      <ul className="mt-2 overflow-hidden rounded-[14px] bg-v3-panel shadow-v3-hairline-strong">
        {reports.map((report) => (
          <li
            key={report.id}
            className="border-b border-v3-border last:border-0"
          >
            <button
              className="flex min-h-[52px] w-full min-w-0 items-center gap-3 px-3.5 py-2 text-left transition-colors hover:bg-v3-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-v3-brand"
              data-testid="dictation-recent-report"
              type="button"
              onClick={() => onOpen(report.id)}
            >
              <FileText
                aria-hidden
                className="size-4 shrink-0 text-v3-text-3"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14.5px] font-medium text-v3-text">
                  {report.title}
                </span>
                {report.when && (
                  <span className="block truncate text-[12.5px] tabular-nums text-v3-text-3">
                    {report.when}
                  </span>
                )}
              </span>
              <ChevronRight
                aria-hidden
                className="size-4 shrink-0 text-v3-text-3"
              />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};

/** Saved, saving, or not saved with Retry (next to the report). */
const SaveState = ({
  status,
  onRetry,
}: {
  status: ReportSaveStatus;
  onRetry?: () => void;
}): JSX.Element | null => {
  const { t } = useTranslation();
  if (status === "idle") return null;
  if (status === "error")
    return (
      <span
        className="inline-flex items-center gap-1 text-v3-rec-text"
        data-testid="dictation-save-state"
        role="status"
      >
        <CloudOff aria-hidden className="size-3.5" />
        {t("dictationReport.save.failed")}
        {onRetry && (
          <button
            className="font-semibold underline underline-offset-2"
            type="button"
            onClick={onRetry}
          >
            {t("dictationReport.retry")}
          </button>
        )}
      </span>
    );
  return (
    <span
      className="inline-flex items-center gap-1"
      data-state={status}
      data-testid="dictation-save-state"
      role="status"
    >
      {status === "saving" ? (
        <Loader2 aria-hidden className="size-3.5 animate-spin" />
      ) : (
        <Check aria-hidden className="size-3.5" />
      )}
      {status === "saving"
        ? t("dictationReport.save.saving")
        : t("dictationReport.save.saved")}
    </span>
  );
};

const Intro = ({
  templates,
  loading,
  onManage,
  recent = [],
  onOpenRecent,
}: {
  templates: readonly DictationTemplate[];
  loading: boolean;
  onManage: () => void;
  recent?: readonly RecentReport[];
  onOpenRecent?: (id: string) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const example = templates[0]?.aliases[0] ?? templates[0]?.name;
  return (
    <div className="mt-6" data-testid="dictation-intro">
      <p className="text-[17px] leading-[26px] text-v3-text-2">
        {t("dictationReport.intro")}
      </p>
      {example && (
        <p className="mt-3 rounded-[14px] bg-v3-surface-2 px-4 py-3 text-[15px] leading-[22px] text-v3-text">
          {t("dictationReport.example", { name: example })}
        </p>
      )}
      {!loading && templates.length === 0 && (
        <div className="mt-4 space-y-3">
          <p className="text-[14px] leading-5 text-v3-text-3">
            {t("dictationReport.noTemplates")}
          </p>
          <Button variant="secondary" onClick={onManage}>
            <FileText />
            {t("dictationReport.templates.new")}
          </Button>
        </div>
      )}
      {templates.length > 0 && (
        <ul
          className="mt-5 space-y-1.5"
          aria-label={t("dictationReport.yourTemplates")}
        >
          {templates.map((template) => (
            <li
              key={template.id}
              className="flex min-w-0 items-baseline gap-2 text-[14px] leading-5"
            >
              <FileText
                aria-hidden
                className="size-3.5 shrink-0 translate-y-0.5 text-v3-text-3"
              />
              <span className="min-w-0">
                <span className="font-semibold text-v3-text">
                  {template.name}
                </span>
                {template.aliases.length > 0 && (
                  <span className="text-v3-text-3">
                    {" "}
                    · {template.aliases.map((alias) => `“${alias}”`).join(", ")}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      <RecentReports reports={recent} onOpen={onOpenRecent} />
    </div>
  );
};

/** The bottom dock: Dictate, the live bar while listening, or busy. */
const Dock = ({
  listening,
  connecting,
  writing,
  seconds,
  stream,
  hasReport,
  onStart,
  onStop,
}: {
  listening: boolean;
  connecting: boolean;
  writing: boolean;
  seconds: number;
  stream: MediaStream | null;
  hasReport: boolean;
  onStart: () => void;
  onStop: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  if (listening)
    return (
      <div
        className={cn(
          "mx-auto flex h-[68px] w-full items-center gap-2.5 rounded-full bg-v3-panel py-2 pl-5 pr-2 font-v3 text-v3-text shadow-v3-float ring-1 ring-v3-border",
          DOCK_MAX_WIDTH,
        )}
        data-testid="dictation-live"
      >
        <Waveform
          className="min-w-0 flex-1"
          label={t("dictationReport.micLevel")}
          stream={stream}
        />
        <p className="flex shrink-0 items-center gap-1.5 font-mono text-[15px] tabular-nums">
          <span aria-hidden className="size-1.5 rounded-full bg-v3-rec" />
          {formatClock(seconds)}
        </p>
        <button
          className="flex h-12 shrink-0 items-center gap-2 rounded-full bg-v3-rec-tint px-5 text-[15px] font-semibold text-v3-rec-text transition-colors hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
          data-testid="dictation-stop"
          type="button"
          onClick={onStop}
        >
          <span aria-hidden className="size-3 rounded-[3px] bg-v3-rec" />
          {t("dictationReport.stop")}
        </button>
      </div>
    );
  return (
    <RecordDock
      busy={connecting || writing}
      data-testid="dictation-start"
      disabled={connecting || writing}
      label={
        writing
          ? t("dictationReport.writing")
          : connecting
            ? t("dictation.connecting")
            : hasReport
              ? t("dictationReport.dictateMore")
              : t("dictationReport.dictate")
      }
      onClick={onStart}
    />
  );
};

const copyText = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

export type DictationReportScreenProps = {
  templates: readonly DictationTemplate[];
  templatesLoading?: boolean;
  state: DictationReportState;
  phase: DictationReportPhase;
  canUndo: boolean;
  durationMs?: number | null;
  // Live dictation.
  status: "idle" | "connecting" | "listening" | "finishing";
  liveText?: string;
  statusMessage?: string | null;
  seconds?: number;
  stream?: MediaStream | null;
  onStart: () => void;
  onStop: () => void;
  onChoose: (template: DictationTemplate) => void;
  onUndo: () => void;
  onRetry: () => void;
  onReset: () => void;
  onClose: () => void;
  onManage: () => void;
  // Saved reports.
  saveStatus?: ReportSaveStatus;
  onRetrySave?: () => void;
  // The report is saved: it can be deleted (as a visit).
  saved?: boolean;
  onDelete?: () => void;
  recent?: readonly RecentReport[];
  onOpenRecent?: (id: string) => void;
  children?: ReactNode;
};

/** The dictation screen, presentational (stories and tests drive it). */
export const DictationReportScreen = ({
  templates,
  templatesLoading = false,
  state,
  phase,
  canUndo,
  durationMs,
  status,
  liveText,
  statusMessage,
  seconds = 0,
  stream = null,
  onStart,
  onStop,
  onChoose,
  onUndo,
  onRetry,
  onReset,
  onClose,
  onManage,
  saveStatus = "idle",
  onRetrySave,
  onDelete,
  recent,
  onOpenRecent,
  children,
}: DictationReportScreenProps): JSX.Element => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);
  const writing = phase === "filling" || status === "finishing";
  const busy = writing || status === "connecting" || status === "listening";
  const report = state.report;
  const pending = !state.template && state.dictations.length > 0;
  return (
    <div
      className="flex min-h-0 w-full flex-1 flex-col self-stretch bg-v3-panel font-v3 text-v3-text"
      data-testid="dictation-report-screen"
    >
      <header className="flex h-14 shrink-0 items-center gap-1 px-1.5">
        <IconButton label={t("dictationReport.back")} onClick={onClose}>
          <ChevronLeft className="!size-5" />
        </IconButton>
        <h2 className="flex min-w-0 flex-1 items-center gap-2 text-[17px] font-semibold">
          <span className="truncate">{t("dictationReport.title")}</span>
          <BetaMark feature="radiology-dictation" />
        </h2>
        {onDelete && (
          <IconButton
            data-testid="dictation-delete"
            disabled={busy}
            label={t("dictationReport.deleteReport.title")}
            onClick={onDelete}
          >
            <Trash2 />
          </IconButton>
        )}
        {report && (
          <IconButton
            data-testid="dictation-new"
            disabled={busy}
            label={t("dictationReport.new")}
            onClick={onReset}
          >
            <RotateCcw />
          </IconButton>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-1 sm:px-5">
        <DictationTemplateChip
          canUndo={canUndo}
          disabled={busy}
          state={state}
          templates={templates}
          onChoose={onChoose}
          onManage={onManage}
          onUndo={onUndo}
        />
        {!report && !pending && (
          <Intro
            loading={templatesLoading}
            recent={recent}
            templates={templates}
            onManage={onManage}
            onOpenRecent={onOpenRecent}
          />
        )}
        {pending && (
          <Notice
            className="mt-4"
            description={t("dictationReport.needTemplateHint")}
            testId="dictation-need-template"
            title={t("dictationReport.needTemplate")}
            tone="info"
          />
        )}
        <div className="mt-4 space-y-3">
          {phase === "error" && (
            <Notice
              actions={
                <Button variant="secondary" onClick={onRetry}>
                  {t("dictationReport.retry")}
                </Button>
              }
              description={t("dictationReport.failedHint")}
              testId="dictation-failed"
              title={t("dictationReport.failed")}
              tone="error"
            />
          )}
          <GuardNotice state={state} />
          {report && (
            <>
              <div className="flex min-w-0 items-center justify-between gap-2">
                <div className="min-w-0 text-[13px] text-v3-text-3">
                  <p
                    className="min-w-0 truncate"
                    data-testid="dictation-report-meta"
                  >
                    {writing
                      ? t("dictationReport.writing")
                      : state.changedLines.length
                        ? t("dictationReport.changed", {
                            count: state.changedLines.length,
                          })
                        : t("dictationReport.defaults")}
                    {!writing &&
                      durationMs != null &&
                      state.changedLines.length > 0 && (
                        <span className="tabular-nums">
                          {" · "}
                          {t("dictationReport.seconds", {
                            seconds: (durationMs / 1000).toFixed(1),
                          })}
                        </span>
                      )}
                  </p>
                  <p className="mt-0.5 truncate text-[12.5px]">
                    <SaveState onRetry={onRetrySave} status={saveStatus} />
                  </p>
                </div>
                <Button
                  data-testid="dictation-copy"
                  disabled={writing}
                  variant="secondary"
                  onClick={() => {
                    void copyText(report).then(setCopied);
                  }}
                >
                  {copied ? <Check /> : <Copy />}
                  {copied
                    ? t("dictationReport.copied")
                    : t("dictationReport.copy")}
                </Button>
              </div>
              <DictationReportView
                busy={writing}
                changedLines={state.changedLines}
                lastChangedLines={state.lastChangedLines}
                report={report}
              />
              <BetaFeedbackButton
                appearance="link"
                feature="radiology-dictation"
              />
            </>
          )}
          {children}
        </div>
      </div>

      <div className="shrink-0 space-y-2 px-3 pb-[max(12px,env(safe-area-inset-bottom))] pt-2 sm:px-4 sm:pb-4 [&>p]:mx-auto [&>p]:max-w-[540px]">
        {(status === "listening" || status === "finishing") && liveText ? (
          <p
            aria-live="polite"
            className="line-clamp-3 px-2 text-[14px] leading-5 text-v3-text-2"
            data-testid="dictation-live-text"
          >
            {liveText}
          </p>
        ) : statusMessage ? (
          <p className="px-2 text-[13.5px] text-v3-text-3" role="status">
            {statusMessage}
          </p>
        ) : (
          <p className="flex items-center gap-1.5 px-2 text-[13px] text-v3-text-3">
            <Mic aria-hidden className="size-3.5" />
            {report
              ? t("dictationReport.hintMore")
              : t("dictationReport.hintFirst")}
          </p>
        )}
        <Dock
          connecting={status === "connecting"}
          hasReport={!!report}
          listening={status === "listening"}
          seconds={seconds}
          stream={stream}
          writing={writing}
          onStart={onStart}
          onStop={onStop}
        />
      </div>
    </div>
  );
};

/**
 * Radiology dictation mode (Voice to Text): say the template name, dictate,
 * Stop; the report is filled in a few seconds. Live words come from the
 * transcriber's fast path (Deepgram nova-3-medical, no per-segment
 * clean-up); the fill is POST /api/v2/dictation-reports.
 */
export type SavedDictatedReport = {
  public_id: string;
  title: string | null;
  result: string | null;
  transcription: string | null;
  customTemplatePublicId: string | null;
};

const DictationReportSession = ({
  templates,
  templatesLoading,
  saved,
  onClose,
  onDeleted,
  onOpenReport,
}: {
  templates: readonly DictationTemplate[];
  templatesLoading: boolean;
  saved?: SavedDictatedReport;
  onClose: () => void;
  onDeleted?: () => void;
  onOpenReport?: (id: string) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const [initial] = useState(() =>
    saved ? reportStateFromVisit(saved, templates) : undefined,
  );
  const session = useDictationReport(templates, initial);
  const saving = useDictatedReportSaving({
    state: session.state,
    phase: session.phase,
    savedId: saved?.public_id ?? null,
    savedTitle: saved?.title ?? null,
  });
  const dictation = useDictation({
    language: "eng",
    provider: TranscriptionProviderName.DEEPGRAM,
    maxSeconds: DICTATION_REPORT_MAX_SECONDS,
  });
  const [manageOpen, setManageOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { files } = useVisitList();
  const recent = files
    .filter(
      (file) => isDictatedReport(file.status) && file.public_id !== saving.id,
    )
    .slice(0, RECENT_REPORTS)
    .map((file) => ({
      id: String(file.public_id),
      title: file.title ?? t("dictationReport.title"),
      when: file.createDate ? getShortDateAndTime(file.createDate) : "",
    }));
  // The final words arrive when the dictation goes back to idle after Stop.
  const previous = useRef(dictation.status);
  const { dictate } = session;
  useEffect(() => {
    const was = previous.current;
    previous.current = dictation.status;
    if (was === "finishing" && dictation.status === "idle" && dictation.text)
      dictate(dictation.text);
  }, [dictation.status, dictation.text, dictate]);
  const live = [dictation.text, dictation.interim].filter(Boolean).join(" ");
  return (
    <>
      <DictationReportScreen
        canUndo={session.canUndo}
        durationMs={session.durationMs}
        liveText={live}
        phase={session.phase}
        recent={recent}
        saveStatus={saving.status}
        saved={!!saving.id}
        seconds={dictation.elapsedSeconds}
        state={session.state}
        status={dictation.status}
        statusMessage={
          dictation.status === "idle"
            ? dictationMessage(t, {
                status: dictation.status,
                error: dictation.error,
                elapsedSeconds: dictation.elapsedSeconds,
              })
            : null
        }
        stream={dictation.stream}
        templates={templates}
        templatesLoading={templatesLoading}
        onChoose={session.chooseTemplate}
        onClose={() => {
          dictation.stop();
          onClose();
        }}
        onDelete={saving.id ? () => setConfirmDelete(true) : undefined}
        onManage={() => setManageOpen(true)}
        onOpenRecent={onOpenReport}
        onReset={() => {
          session.reset();
          saving.forget();
        }}
        onRetry={session.retry}
        onRetrySave={saving.retry}
        onStart={() => {
          void dictation.start();
        }}
        onStop={dictation.stop}
        onUndo={session.undo}
      />
      <DictationTemplatesSheetV32
        open={manageOpen}
        templates={templates}
        onOpenChange={setManageOpen}
      />
      <ConfirmSheet
        busy={deleting}
        cancelLabel={t("dictationReport.templates.cancel")}
        confirmLabel={t("dictationReport.deleteReport.confirm")}
        description={t("dictationReport.deleteReport.hint")}
        destructive
        open={confirmDelete}
        testId="dictation-delete-report"
        title={t("dictationReport.deleteReport.title")}
        onConfirm={() => {
          const id = saving.id;
          if (!id) return;
          setDeleting(true);
          void deleteDictatedReport(id)
            .then(() => {
              session.reset();
              saving.forget();
              onDeleted?.();
            })
            .catch(() => {
              toast({
                variant: "destructive",
                title: t("dictationReport.deleteReport.failed"),
              });
            })
            .finally(() => {
              setDeleting(false);
              setConfirmDelete(false);
            });
        }}
        onOpenChange={setConfirmDelete}
      />
    </>
  );
};

/**
 * Radiology dictation mode (Voice to Text): say the template name, dictate,
 * Stop; the report is filled in a few seconds and saved as a visit. Live
 * words come from the transcriber's fast path (Deepgram nova-3-medical, no
 * per-segment clean-up); the fill is POST /api/v2/dictation-reports.
 * `saved` opens a saved report again, to continue it.
 */
export const DictationReportV32 = ({
  onClose,
  saved,
  onDeleted,
  onOpenReport,
}: {
  onClose: () => void;
  saved?: SavedDictatedReport;
  onDeleted?: () => void;
  onOpenReport?: (id: string) => void;
}): JSX.Element => {
  const { templates, loading } = useDictationTemplates();
  // A saved report needs its template to continue: wait for the list.
  if (saved && loading)
    return (
      <div
        aria-busy
        className="flex min-h-0 w-full flex-1 items-center justify-center bg-v3-panel"
        data-testid="dictation-report-loading"
      >
        <Loader2 aria-hidden className="size-5 animate-spin text-v3-text-3" />
      </div>
    );
  return (
    <DictationReportSession
      key={saved?.public_id ?? "new"}
      saved={saved}
      templates={templates}
      templatesLoading={loading}
      onClose={onClose}
      onDeleted={onDeleted}
      onOpenReport={onOpenReport}
    />
  );
};
